import { useState, useEffect, useCallback } from "react";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import { observer } from "mobx-react";
import ActualProgressEntry from "../model/ActualProgressEntry";
import PlannedProgressEntry from "../model/PlannedProgressEntry";
import { Box } from "@mui/material";
import { Tooltip } from "@mui/material";
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { currencyFormat, percentFormat} from '../util/Formatters'
import { useProjectStore } from "../model/ProjectStore";
import { autorun, action, runInAction } from "mobx";


const generateActualsRow = (task, data) => {
  let maxPeriod = task.plannedProgress
    .map((x) => x.period)
    .reduce((x, y) => Math.max(x, y));

  let allCumProg = data.map((x) => x.actualCumProgress);
  let recentCumProg = allCumProg[allCumProg.length - 1];

  let allCumCost = data.map((x) => x.actualCumCost);
  let recentCumCost = allCumCost[allCumCost.length - 1];

  let newActual = new ActualProgressEntry(
    task.project,
    task,
    maxPeriod + 1,
    0,
    0,
    0,
    ""
  ); //constructor(project, task, period, progress, actualCost, anticipatedCost, note)
  let newPlan = new PlannedProgressEntry(
    task.project,
    task,
    maxPeriod + 1,
    0,
    0,
    0,
    false
  ); //constructor(project, task, period, expectedProgress, expectedCost, periodWeight, nonWorkingPeriod, taskPackage)
  task.plannedProgress.push(newPlan);
  task.actualProgress.push(newActual);

  return {
    id: maxPeriod + 1,
    periodMonth: task.project.periodToDateLookup[maxPeriod + 1],
    expectedProgress: 0,
    progress: 0,
    cumulativeProgress: recentCumProg,
    expectedCost: 0,
    actualCost: 0,
    cumulativeCost: recentCumCost,
    note: "",
  };
};

const generateGridRow = (
  task,
  plannedProgressEntry,
  index,
  readOnly,
  setIsTaskPackage
) => {
  const period = plannedProgressEntry.period;
  const actualProgressEntry = task.getActualProgressForPeriod(period);
  const expectedProgress = plannedProgressEntry.expectedProgress;
  const expectedCost = plannedProgressEntry.expectedCost;

  const actualProgress = task.actualProgress.filter((ap) => ap.period > 0);
  let cumulativeProgress = 0.0;
  let cumulativeCost = 0.0;
  for (let i = 1; i <= index + 1; i++) {
    if (actualProgress.length >= i) {
      cumulativeProgress += !isNaN(parseFloat(actualProgress[i - 1].progress))
        ? parseFloat(actualProgress[i - 1].progress)
        : 0;
      cumulativeCost += !isNaN(parseFloat(actualProgress[i - 1].actualCost))
        ? parseFloat(actualProgress[i - 1].actualCost) //parseInt(actualProgress[i - 1].actualCost)
        : 0;
    }
  }
  setIsTaskPackage(readOnly);

  return {
    period: period,
    periodMonth: task.project.periodToDateLookup[period],
    expectedProgress: expectedProgress,
    progress:
      actualProgressEntry.progress === null ? "" : actualProgressEntry.progress,
    actualCumProgress: cumulativeProgress.toFixed(2),
    cumulativeProgress: cumulativeProgress.toFixed(2),
    expectedCost: expectedCost,
    actualCost: actualProgressEntry.actualCost ?? 0,
    actualCumCost: cumulativeCost,
    cumulativeCost: cumulativeCost,
    note: actualProgressEntry.note,
  };
};



const TaskActuals = ({ task, readOnly }) => {
  const [data, setData] = useState([]);
  const [isTaskPackage, setIsTaskPackage] = useState(true);
  const [rowSelectionModel, setRowSelectionModel] = useState({ids: new Set([task?.project?.dataPeriod ?? 1]), type: 'include', isSelected: true});
  const projectStore = useProjectStore();

  const apiRef = useGridApiRef();
  useEffect(() =>
    {
      setData(
        task.plannedProgress
          .filter((pp) => pp.period > 0)
          .map((entry, index) =>
            generateGridRow(
              task,
              entry,
              index,
              readOnly,
              setIsTaskPackage
            )
          )
      );
      setTimeout(scrollAndSelect, 200);
    }, [readOnly, task, task.actualProgress]);

    const scrollAndSelect = useCallback(()=> {
      const proj = task.project;
      const dataPeriod = proj.dataPeriod + "";
      const periods = task.actualProgress.map(ap => ap.period);
      const minPeriod = Math.min.apply(Math, periods);
      const scrollIndex = dataPeriod - minPeriod;
      if (scrollIndex > 1) {
        apiRef.current.scrollToIndexes({rowIndex:scrollIndex, colIndex: 0});
        setRowSelectionModel({ids: new Set([dataPeriod]), type: 'include', isSelected: true})
      }
      
    });

    const processRowUpdate = useCallback(action((newRow, oldRow) => { //This mess is to handle editing the total cost and converting it back into cost adjustments.
      const period = newRow.id*1; 
      let actualCost = newRow.actualCost;
      let cumulativeCost = newRow.cumulativeCost
      if (newRow.cumulativeCost != oldRow.cumulativeCost) {
        
        const prevRow = rows.find(r => r.id == (period - 1));
        const nextRow = rows.find(r => r.id == (period + 1));
        
        if (prevRow == null) {
          actualCost = cumulativeCost;
        } else if (nextRow == null || cumulativeCost < nextRow.cumulativeCost) {
          actualCost = cumulativeCost - prevRow.cumulativeCost;
        } else {
          actualCost = cumulativeCost - prevRow.cumulativeCost;
          const nextProgressEntry = task.getActualProgressForPeriod( period + 1 );
          nextProgressEntry.actualCost = nextRow.cumulativeCost - cumulativeCost;
        }
        
      } 

      if(newRow.actualCost != oldRow.actualCost) {
        const prevCost = rows.find(r => r.id == (period - 1))?.cumulativeCost ?? 0;
        cumulativeCost = prevCost*1 + newRow.actualCost*1;
        actualCost = newRow.actualCost
      }

      const actualProgressEntry = task.getActualProgressForPeriod(period);
      actualProgressEntry.actualCost = actualCost;
      newRow.actualCost = actualCost;
      newRow.cumulativeCost = cumulativeCost;

      return newRow;

    }));

  const rows = data.map((row) => ({
    id: row.period + "",
    periodDate : row.periodMonth,
    expectedProgress: row.expectedProgress,
    progress: row.progress,
    cumulativeProgress: row.cumulativeProgress,
    expectedCost: row.expectedCost,
    actualCost: row.actualCost,
    cumulativeCost: row.cumulativeCost,
    note: row.note,
  }));

  const handleAddRow = (task, data, isTaskPackage) => {
    if (!isTaskPackage) {
      const periods = task.plannedProgress.map((x) => x.period);
      const maxPeriod = periods.length > 0 ? Math.max(...periods) : 0;
      const nextPeriod = maxPeriod + 1;

      runInAction(() => {
        task.plannedProgress.push(
          new PlannedProgressEntry(task.project, task, nextPeriod, 0, 0, 0, false)
        );
        task.actualProgress.push(
          new ActualProgressEntry(task.project, task, nextPeriod, 0, 0, 0, '')
        );
      });
    }
  };
  
  const getActualProgress = (value, row) => {
    const period = row.id;
    const actualProgressEntry = task.getActualProgressForPeriod(period);
    return actualProgressEntry.progress ?? 0;
  }

  const setActualProgress = (value, row) => {
    const period = row.id;
    const actualProgressEntry = task.getActualProgressForPeriod(period);
    actualProgressEntry.progress = value;
    var actualProgress = value;
    return { ...row, actualProgress}
  }

  const setNote = (value, row) => {
    const period = row.id;
    const actualProgressEntry = task.getActualProgressForPeriod(period);
    actualProgressEntry.note = value;
    let note = value;
    return { ...row, note}
  }

  const columns = [
    {
      field: "id",
      headerName: "Period",
      type: "number",
      editable: false,
      width: 80,
      align: "left",
      headerAlign: "left",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "periodDate",
      headerName: "Month",
      type: "text",
      editable: false,
      width: 100,
      align: "left",
      headerAlign: "left",
      cellClassName: "uneditableExp",
    },
    {
      field: "expectedProgress",
      headerName: <span> Progress<br /> (planned)</span>,
      type: "number",
      editable: false,
      width: 100,
      align: "right",
      headerAlign: "right",
      cellClassName: "uneditableExp",
      valueFormatter: percentFormat
    },
    {
      field: "progress",
      headerName: <span> Progress<br />(period)</span>,
      type: "number",
      editable: !isTaskPackage,
      width: 100,
      align: "right",
      headerAlign: "right",
      valueGetter: getActualProgress,
      valueSetter: setActualProgress,
      valueFormatter: percentFormat,
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "cumulativeProgress",
      headerName: <span> Progress<br />(cumulative)</span>,
      editable: false,
      width: 100,
      align: "right",
      headerAlign: "right",
      valueFormatter: percentFormat,
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "expectedCost",
      headerName: <span> Planned Cost<br />(period)</span>,
      type: "number",
      editable: false,
      width: 140,
      align: "right",
      headerAlign: "right",
      cellClassName: "uneditableExp",
      valueFormatter: currencyFormat,
    },
    {
      field: "actualCost",
      type: "number",
      headerName: <span> Actual Cost <br />(period)</span>,
      editable: !isTaskPackage,
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat,
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "cumulativeCost",
      headerName: <span> Actual Cost <br />(cumulative)</span>,
      type: "number",
      editable: !isTaskPackage,
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat,
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "note",
      headerName: "Note",
      editable: true,
      width: 200,
      align: "left",
      headerAlign: "center",
      cellClassName: "editable",
      valueSetter: setNote,
      renderCell: (params) => (
        <Tooltip title={params.value ?? " "}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Box
        sx={{
          height: 600,
          width: 1250,
          "& .editable": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#000000",
          },
          "& .uneditable": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#000000",
          },
          "& .uneditableExp": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#808080",
            fontStyle: "italic",
          },
          "& .uneditablePackg": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#808080",
          },
        }}
      >
        <DataGrid
          title="Task Data Grid"
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[15]}
          pageSize={100}
          pagination
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => alert(error)}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>
      <IconButton onClick={() => handleAddRow(task, data, isTaskPackage)}>
        <AddIcon />
      </IconButton>
    </div>
  );
};

export default observer(TaskActuals);